/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import Keycloak from "keycloak-js";

/**
 * vueKeyCloak is a plugin for integrating Keycloak with Vue.js applications.
 * It handles initialization, authentication management, and token updates
 * for seamless integration with Keycloak.
 *
 * @property {function} install - Installs the vueKeyCloak plugin into a Vue application.
 *
 * The `install` method:
 * - Initializes the Keycloak instance with the provided options.
 * - Handles events such as authentication success, logout, and token updates.
 * - Provides hooks for custom functionality during different lifecycle events.
 *
 * @param {Object} app - The Vue application instance.
 * @param {Object} options - An object containing configuration options for Keycloak.
 * @param {Object} options.init - The Keycloak initialization options.
 * @param {function} [options.onReady] - Callback function triggered when Keycloak is ready.
 *   Receives the Keycloak instance as an argument.
 * @param {function} [options.onAuthLogout] - Callback function triggered on user logout.
 *   Receives the Keycloak instance as an argument.
 * @param {function} [options.onInitSuccess] - Callback function triggered when Keycloak
 *   is initialized successfully. Receives a boolean indicating authentication status.
 * @param {function} [options.onInitError] - Callback function triggered when an error
 *   occurs during Keycloak initialization. Receives the error and additional details.
 */
const vueKeyCloak = {
  install(app, options) {
    console.log("load Keycloak With", options.init);
    const keycloak = new Keycloak(options.init);

    keycloak.onReady = () => {
      if (typeof options.onReady === "function") {
        options.onReady(keycloak);
      }
    };

    let updateTokenInterval;

    keycloak.onAuthSuccess = () => {
      updateTokenInterval = setInterval(() => {
        keycloak
          .updateToken(60)
          .then((updated) => {
            if (options.init.enableLogging) {
              console.log(
                `[vue-keycloak-js] Token ${updated ? "updated" : "not updated"}`,
              );
            }
          })
          .catch((error) => {
            if (options.init.enableLogging) {
              console.error("[vue-keycloak-js] Error updating token:", error);
            }
            keycloak.clearToken();
          });
      }, 10000);
    };

    keycloak.onAuthLogout = () => {
      clearInterval(updateTokenInterval);
      if (typeof options.onAuthLogout === "function") {
        options.onAuthLogout(keycloak);
      }
    };

    keycloak
      .init(options.init)
      .then((authenticated) => {
        if (typeof options.onInitSuccess === "function") {
          options.onInitSuccess(authenticated);
        }
      })
      .catch((err) => {
        const error = new Error("Failure during initialization of Keycloak");
        if (typeof options.onInitError === "function") {
          options.onInitError(error, err);
        } else {
          console.error(error, err);
        }
      });
  },
};

export default vueKeyCloak;
